// Restroom visits as a line: a running total (default) or visits per interval, with gates open and
// game start marked. For one restroom, the running total also shows where each cleaning came due
// (every CLEANING_THRESHOLD visits).
import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from 'chart.js';
import { ChartHeader, ChartSegmented, ResolutionToggle, applyChartTheme, CHART_THEME, hoverGuidePlugin, sectionLineDataset } from '../../Concession/charts/chartTheme';
import { PHASES } from '../../Retail/retailPalette';
import { visitFlow, minuteToClock, clockToMinute, CLEANING_THRESHOLD } from '../facilitiesMetrics';

ChartJS.register(annotationPlugin);

export const VISITS_COLOR = '#3e58c0';
const DUE_COLOR = '#d97706';

const INTERVALS = [1, 5, 15, 30, 60];
const MODES = [
  { value: 'total', label: 'Running total' },
  { value: 'interval', label: 'Per interval' },
];

const shortClock = (minute) => minuteToClock(minute).replace(':00 ', ' ');

// Dashed vertical marker at a game-day minute; x is a (fractional) point index
const markAt = (x, label, color) => ({
  type: 'line',
  scaleID: 'x',
  value: x,
  borderColor: color,
  borderWidth: 1.5,
  borderDash: [6, 4],
  label: {
    display: true,
    content: label,
    position: 'start',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    color,
    font: { family: CHART_THEME.font, size: 11, weight: '600' },
    padding: { x: 6, y: 2 },
  },
});

// Dashed horizontal line at each cleaning threshold the running total reached
const dueLine = (y, label) => ({
  type: 'line',
  scaleID: 'y',
  value: y,
  borderColor: DUE_COLOR,
  borderWidth: 1,
  borderDash: [4, 4],
  label: {
    display: true,
    content: label,
    position: 'end',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    color: DUE_COLOR,
    font: { family: CHART_THEME.font, size: 11, weight: '600' },
    padding: { x: 6, y: 2 },
  },
});

// `range` ({ start, end } in game-day minutes) limits the chart to the analysis window; the running
// total then starts from zero at the window's start, matching the window's numbers above.
// `who` names where the visits were ("all restrooms", or a restroom) for the subtitle.
// `showDue` draws the cleaning thresholds (for a single restroom).
const VisitsChart = ({ series, currentGame, range = null, who = 'all restrooms', title = 'Entries', height = 300, showDue = false }) => {
  const [mode, setMode] = useState('total');
  const [interval, setIntervalSize] = useState(1);
  const isTotal = mode === 'total';
  const size = isTotal ? 1 : interval;

  const points = useMemo(() => {
    const all = visitFlow(series || [], size);
    const buckets = range ? all.filter((b) => b.end > range.start && b.start < range.end) : all;
    if (!isTotal) return buckets.map((b) => ({ ...b, at: b.start }));
    let sum = 0;
    return buckets.map((b) => {
      sum += b.visits;
      return { ...b, visits: sum, at: b.end };
    });
  }, [series, size, range, isTotal]);

  const marks = useMemo(() => {
    if (!points.length) return [];
    const first = points[0].at;
    const last = points[points.length - 1].at;
    const toX = (minute) => (minute - first) / size;
    const inRange = (minute) => minute != null && minute >= first - size && minute <= last;
    const gates = clockToMinute(currentGame?.gatesOpen);
    const start = clockToMinute(currentGame?.gameStartTime);
    const lines = [
      ...(inRange(gates) ? [markAt(toX(gates), 'Gates open', PHASES.gates.color)] : []),
      ...(inRange(start) ? [markAt(toX(start), 'Game start', CHART_THEME.gameStart)] : []),
    ];
    if (showDue && isTotal) {
      const top = points[points.length - 1].visits;
      for (let y = CLEANING_THRESHOLD; y <= top; y += CLEANING_THRESHOLD) {
        const reached = points.find((p) => p.visits >= y);
        lines.push(dueLine(y, `Cleaning ${y / CLEANING_THRESHOLD} due${reached ? ` · ${minuteToClock(reached.start)}` : ''}`));
      }
    }
    return lines;
  }, [points, size, currentGame?.gatesOpen, currentGame?.gameStartTime, showDue, isTotal]);

  const data = useMemo(() => ({
    labels: points.map((p) => p.at),
    datasets: [{ label: 'Entries', data: points.map((p) => p.visits), ...sectionLineDataset(VISITS_COLOR) }],
  }), [points]);

  const options = useMemo(() => applyChartTheme({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          // Hourly labels, whatever the interval
          callback: (value, index) => {
            const at = points[index]?.at;
            if (at == null) return '';
            return at % 60 === 0 ? shortClock(at) : '';
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, callback: (v) => Number(v).toLocaleString() },
        title: { display: true, text: isTotal ? 'Entries so far' : `Entries per ${interval} min` },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const p = points[items[0]?.dataIndex];
            if (!p) return '';
            return isTotal ? `As of ${minuteToClock(p.at)}` : `${minuteToClock(p.start)} – ${minuteToClock(p.end)}`;
          },
          label: (ctx) => ` Entries: ${Number(ctx.raw).toLocaleString()}`,
        },
      },
      annotation: { annotations: marks },
    },
  }), [points, isTotal, interval, marks]);

  const subtitle = isTotal
    ? `Running total of entries to ${who}${range ? ' since the start of the window' : ''}.${showDue ? ` A cleaning is due every ${CLEANING_THRESHOLD} entries.` : ''}`
    : `Entries to ${who} in each ${interval}-minute interval.`;

  return (
    <div className="graph panel-2 cx-chart-card">
      <ChartHeader
        title={title}
        subtitle={subtitle}
        controls={(
          <>
            <ChartSegmented options={MODES} value={mode} onChange={setMode} />
            {!isTotal && <ResolutionToggle resolutions={INTERVALS} value={interval} onChange={setIntervalSize} />}
          </>
        )}
      />
      {points.length ? (
        <div style={{ position: 'relative', height }}>
          <Line data={data} options={options} plugins={[hoverGuidePlugin]} />
        </div>
      ) : (
        <div className="fx-chart-empty">No entries recorded{range ? ' in the selected window' : ' for this game'}.</div>
      )}
    </div>
  );
};

export default VisitsChart;
